import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  User, FileText, Bell, Car, HandCoins, Ship, LogOut, Loader2, Download, Upload, Trash2, CheckCircle2,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getUser, authHeaders, clearAuth, API } from "@/lib/auth";

const DOC_CATEGORIES = [
  "Identificação", "Documentos pessoais", "Comprovativos de rendimento",
  "Comprovativos de morada", "Documentação automóvel", "Documentação de financiamento",
  "Outros documentos",
];

const IMPORT_STAGES = ["Pedido recebido","Em análise","Pesquisa de viaturas","Viatura encontrada","Proposta enviada","Aguardar aprovação do cliente","Viatura reservada","Em transporte","Em legalização","Pronto para entrega","Processo concluído"];
const FIN_STAGES = ["Pedido recebido","Documentação em análise","Documentação incompleta","A aguardar documentos do cliente","Em análise pela Solution Car Premium","Enviado à instituição financeira","Em análise pela instituição financeira","Financiamento aprovado","Financiamento recusado","A aguardar assinatura do contrato","Financiamento concluído"];

const DOC_STATUS = {
  pendente: { label: "Em validação", cls: "bg-amber-50 text-amber-700" },
  aprovado: { label: "Aprovado", cls: "bg-emerald-50 text-emerald-700" },
  rejeitado: { label: "Rejeitado", cls: "bg-red-50 text-red-700" },
  reenviar: { label: "Reenviar", cls: "bg-blue-50 text-blue-700" },
};

export default function ClientAreaPage() {
  const navigate = useNavigate();
  const user = getUser();
  const [tab, setTab] = useState("perfil");
  const [profile, setProfile] = useState(null);
  const [processes, setProcesses] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selProcess, setSelProcess] = useState(null);
  const [msgText, setMsgText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [docCategory, setDocCategory] = useState(DOC_CATEGORIES[0]);

  useEffect(() => {
    if (!user) { navigate("/entrar"); return; }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    try {
      const [p, pr, d, n] = await Promise.all([
        axios.get(`${API}/client/profile`, { headers: authHeaders() }),
        axios.get(`${API}/client/processes`, { headers: authHeaders() }),
        axios.get(`${API}/client/documents`, { headers: authHeaders() }),
        axios.get(`${API}/client/notifications`, { headers: authHeaders() }),
      ]);
      setProfile(p.data);
      setProcesses(pr.data);
      setDocuments(d.data);
      setNotifications(n.data);
    } catch (err) {
      if (err?.response?.status === 401) { clearAuth(); navigate("/entrar"); }
    } finally {
      setLoading(false);
    }
  };

  const openProcess = async (p) => {
    try {
      const { data } = await axios.get(`${API}/client/processes/${p.id}`, { headers: authHeaders() });
      setSelProcess(data);
    } catch { toast.error("Erro ao abrir o processo."); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msgText.trim()) return;
    try {
      await axios.post(`${API}/client/processes/${selProcess.id}/messages`, { body: msgText }, { headers: authHeaders() });
      setMsgText("");
      await openProcess(selProcess);
    } catch { toast.error("Erro ao enviar mensagem."); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/client/profile`, profile, { headers: authHeaders() });
      toast.success("Dados atualizados.");
    } catch { toast.error("Erro ao guardar."); }
  };

  const uploadDocs = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const data = new FormData();
      files.forEach((f) => data.append("files", f));
      await axios.post(`${API}/client/documents?category=${encodeURIComponent(docCategory)}`, data, { headers: authHeaders() });
      toast.success(`${files.length} documento(s) enviado(s).`);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar documentos.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const unread = notifications.filter((n) => !n.read).length;
  const markRead = async () => {
    await axios.post(`${API}/client/notifications/read-all`, {}, { headers: authHeaders() });
    setNotifications((n) => n.map((x) => ({ ...x, read: true })));
  };

  const logout = () => { clearAuth(); navigate("/"); };

  if (!user) return null;
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const TABS = [
    { id: "perfil", label: "Perfil", icon: User },
    { id: "processos", label: "Os Meus Processos", icon: FileText },
    { id: "documentos", label: "Documentos", icon: Upload },
    { id: "notificacoes", label: "Notificações", icon: Bell, badge: unread },
  ];

  const stages = (p) => (p.type === "importacao" ? IMPORT_STAGES : FIN_STAGES);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="client-area">
      <Header />
      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-display text-3xl text-[#0072C6]">Área de Cliente</h1>
              <p className="text-sm text-slate-500 mt-1">Olá, {profile?.name}</p>
            </div>
            <button onClick={logout} data-testid="client-logout" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0072C6]">
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="client-tabs">
            {TABS.map(({ id, label, icon: Icon, badge }) => (
              <button key={id} data-testid={`client-tab-${id}`} onClick={() => setTab(id)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium border transition-colors ${
                  tab === id ? "bg-[#0072C6] text-white border-[#0072C6]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0072C6] hover:text-[#0072C6]"
                }`}>
                <Icon className="w-4 h-4" /> {label}
                {badge > 0 && <span className="bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">{badge}</span>}
              </button>
            ))}
          </div>

          {/* PERFIL */}
          {tab === "perfil" && profile && (
            <form onSubmit={saveProfile} className="bg-white border border-slate-200 p-7 sm:p-10 max-w-3xl" data-testid="profile-form">
              <h2 className="font-display text-2xl text-[#0072C6] mb-7">Dados Pessoais</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Nome completo</label>
                  <input data-testid="p-name" value={profile.name || ""} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Email</label>
                  <input value={profile.email} disabled className="w-full border border-slate-200 px-4 py-3 text-sm bg-slate-50 text-slate-400" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">NIF</label>
                  <input data-testid="p-nif" value={profile.nif || ""} onChange={(e) => setProfile((p) => ({ ...p, nif: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Telefone</label>
                  <input data-testid="p-phone" value={profile.phone || ""} onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Morada</label>
                  <input data-testid="p-address" value={profile.address || ""} onChange={(e) => setProfile((p) => ({ ...p, address: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Código postal</label>
                  <input data-testid="p-postal" value={profile.postal_code || ""} onChange={(e) => setProfile((p) => ({ ...p, postal_code: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
              </div>
              <button type="submit" data-testid="profile-save"
                className="mt-7 bg-[#0072C6] text-white font-semibold px-8 py-3.5 text-sm hover:bg-[#005FA3] transition-colors">
                Guardar Alterações
              </button>
            </form>
          )}

          {/* PROCESSOS */}
          {tab === "processos" && (
            <div className="grid lg:grid-cols-12 gap-6">
              <div className="lg:col-span-5 space-y-3" data-testid="process-list">
                {processes.length === 0 ? (
                  <div className="bg-white border border-slate-200 p-8 text-center text-slate-400 text-sm">
                    Ainda não tem processos. <Link to="/#importacao" className="text-[#0072C6] underline">Solicite uma importação</Link> ou <Link to="/#financiamento" className="text-[#0072C6] underline">financiamento</Link>.
                  </div>
                ) : processes.map((p) => (
                  <button key={p.id} onClick={() => openProcess(p)} data-testid={`process-card-${p.id}`}
                    className={`w-full text-left bg-white border p-5 transition-colors ${selProcess?.id === p.id ? "border-[#0072C6]" : "border-slate-200 hover:border-[#0072C6]/40"}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        {p.type === "importacao" ? <Ship className="w-5 h-5 text-[#0072C6]" /> : <HandCoins className="w-5 h-5 text-[#0072C6]" />}
                        <div>
                          <p className="font-semibold text-sm text-[#0072C6] capitalize">
                            {p.type === "importacao" ? "Importação" : "Financiamento"} {p.data?.brand ? `— ${p.data.brand} ${p.data.model || ""}` : ""}
                          </p>
                          <p className="text-xs text-slate-400">{new Date(p.created_at).toLocaleDateString("pt-PT")}</p>
                        </div>
                      </div>
                      <span className="text-[11px] font-semibold uppercase tracking-wider bg-[#0072C6] text-white px-2.5 py-1">{p.status}</span>
                    </div>
                  </button>
                ))}
              </div>

              <div className="lg:col-span-7">
                {!selProcess ? (
                  <div className="bg-white border border-dashed border-slate-200 p-12 text-center text-slate-400 text-sm" data-testid="process-empty">
                    Selecione um processo para ver o acompanhamento.
                  </div>
                ) : (
                  <div className="bg-white border border-slate-200 p-6 sm:p-8" data-testid="process-detail">
                    <h3 className="font-display text-xl text-[#0072C6] mb-1 capitalize">
                      {selProcess.type === "importacao" ? "Importação" : "Financiamento"} — Acompanhamento
                    </h3>
                    <p className="text-xs text-slate-400 mb-6">Atualizado em {new Date(selProcess.updated_at).toLocaleString("pt-PT")}</p>

                    {/* Timeline */}
                    <div className="space-y-0" data-testid="process-timeline">
                      {stages(selProcess).map((stage, i) => {
                        const history = selProcess.status_history || [];
                        const reached = history.findIndex((h) => h.status === stage);
                        const currentIdx = history.length - 1;
                        const isCurrent = history[currentIdx]?.status === stage;
                        return (
                          <div key={stage} className="flex gap-4">
                            <div className="flex flex-col items-center">
                              <span className={`w-4 h-4 rounded-full border-2 mt-0.5 ${isCurrent ? "bg-[#0072C6] border-[#0072C6]" : reached >= 0 ? "bg-[#0072C6]/40 border-[#0072C6]/40" : "bg-white border-slate-300"}`} />
                              {i < stages(selProcess).length - 1 && <span className={`w-0.5 flex-1 ${reached >= 0 ? "bg-[#0072C6]/30" : "bg-slate-200"}`} />}
                            </div>
                            <div className="pb-5">
                              <p className={`text-sm ${isCurrent ? "font-semibold text-[#0072C6]" : reached >= 0 ? "text-slate-600" : "text-slate-400"}`}>{stage}</p>
                              {isCurrent && history[currentIdx]?.note && <p className="text-xs text-slate-500 mt-1">{history[currentIdx].note}</p>}
                              {isCurrent && <p className="text-[11px] text-slate-400 mt-0.5">{new Date(history[currentIdx].at).toLocaleString("pt-PT")} · {history[currentIdx].by}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Messages */}
                    <div className="mt-6 pt-6 border-t border-slate-100">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">Mensagens</h4>
                      <div className="space-y-3 max-h-60 overflow-y-auto mb-4" data-testid="process-messages">
                        {(selProcess.messages || []).length === 0 && <p className="text-sm text-slate-400">Sem mensagens ainda.</p>}
                        {(selProcess.messages || []).map((m) => (
                          <div key={m.id} className={`text-sm p-3 max-w-[85%] ${m.sender === "cliente" ? "bg-[#0072C6] text-white ml-auto" : "bg-[#F8FAFC] text-slate-700 border border-slate-200"}`}>
                            <p className="text-[10px] opacity-70 mb-1">{m.sender_name} · {new Date(m.created_at).toLocaleString("pt-PT")}</p>
                            {m.body}
                          </div>
                        ))}
                      </div>
                      <form onSubmit={sendMessage} className="flex gap-2">
                        <input data-testid="process-message-input" value={msgText} onChange={(e) => setMsgText(e.target.value)}
                          placeholder="Escreva uma mensagem…" className="flex-1 border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                        <button type="submit" data-testid="process-message-send" className="bg-[#0072C6] text-white px-5 py-3 text-sm font-semibold hover:bg-[#005FA3]">Enviar</button>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* DOCUMENTOS */}
          {tab === "documentos" && (
            <div className="max-w-4xl">
              <div className="bg-white border border-slate-200 p-6 sm:p-8 mb-6" data-testid="upload-panel">
                <h2 className="font-display text-xl text-[#0072C6] mb-2">Enviar Documentos</h2>
                <p className="text-sm text-slate-500 mb-5">PDF, imagens (JPG, PNG, WEBP), Word, Excel e texto. Sem limite de quantidade — cada ficheiro até 25 MB.</p>
                <div className="flex flex-wrap items-center gap-4">
                  <select data-testid="doc-category" value={docCategory} onChange={(e) => setDocCategory(e.target.value)}
                    className="border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]">
                    {DOC_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                  <label data-testid="doc-upload-button"
                    className="inline-flex items-center gap-2 bg-[#0072C6] text-white text-sm font-semibold px-6 py-3 cursor-pointer hover:bg-[#005FA3] transition-colors">
                    <Upload className="w-4 h-4" /> {uploading ? "A enviar…" : "Escolher ficheiros"}
                    <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx,.txt,.csv" className="hidden" onChange={uploadDocs} disabled={uploading} />
                  </label>
                </div>
              </div>

              <div className="bg-white border border-slate-200" data-testid="documents-list">
                <div className="px-6 py-4 border-b border-slate-100 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Os meus documentos ({documents.length})
                </div>
                {documents.length === 0 ? (
                  <p className="p-8 text-center text-sm text-slate-400">Ainda não enviou documentos.</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {documents.map((d) => (
                      <li key={d.id} className="px-6 py-4 flex flex-wrap items-center gap-4" data-testid={`doc-${d.id}`}>
                        <FileText className="w-5 h-5 text-[#0072C6] shrink-0" />
                        <div className="flex-1 min-w-[200px]">
                          <p className="text-sm font-medium text-[#0072C6]">{d.original_filename}</p>
                          <p className="text-xs text-slate-400">{d.category} · {new Date(d.created_at).toLocaleString("pt-PT")}</p>
                        </div>
                        <span className={`text-[11px] font-semibold px-2.5 py-1 ${DOC_STATUS[d.status]?.cls || ""}`}>
                          {DOC_STATUS[d.status]?.label || d.status}
                        </span>
                        <a href={`${API}/documents/${d.id}/download`} data-testid={`doc-download-${d.id}`}
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              const res = await axios.get(`${API}/documents/${d.id}/download`, { headers: authHeaders(), responseType: "blob" });
                              const url = URL.createObjectURL(res.data);
                              const a = document.createElement("a");
                              a.href = url; a.download = d.original_filename; a.click();
                              URL.revokeObjectURL(url);
                            } catch { toast.error("Erro ao transferir."); }
                          }}
                          className="p-2 text-slate-500 hover:text-[#0072C6]" aria-label="Transferir">
                          <Download className="w-4 h-4" />
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* NOTIFICAÇÕES */}
          {tab === "notificacoes" && (
            <div className="max-w-3xl">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display text-xl text-[#0072C6]">Notificações</h2>
                {unread > 0 && (
                  <button onClick={markRead} data-testid="mark-read" className="text-xs font-semibold text-[#0072C6] underline">Marcar todas como lidas</button>
                )}
              </div>
              <div className="space-y-3" data-testid="notifications-list">
                {notifications.length === 0 && <p className="text-sm text-slate-400 bg-white border border-slate-200 p-6 text-center">Sem notificações.</p>}
                {notifications.map((n) => (
                  <div key={n.id} className={`bg-white border p-5 flex gap-4 ${n.read ? "border-slate-200" : "border-[#0072C6]/40 border-l-4"}`} data-testid={`notification-${n.id}`}>
                    <Bell className={`w-5 h-5 shrink-0 ${n.read ? "text-slate-300" : "text-[#0072C6]"}`} />
                    <div>
                      <p className={`text-sm ${n.read ? "text-slate-600" : "font-semibold text-[#0072C6]"}`}>{n.title}</p>
                      <p className="text-xs text-slate-500 mt-1">{n.body}</p>
                      <p className="text-[11px] text-slate-400 mt-1.5">{new Date(n.created_at).toLocaleString("pt-PT")}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
